import { type ReactElement, useState } from 'react';
import { useFeatureGate } from '@atlassian/feature-gates-react';
import SectionMessage from '@atlaskit/section-message';
import Button from '@atlaskit/button/new';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { defineMessages, useIntl } from 'react-intl';
import { getEnv } from '../../../../../config/env';
import { useSelectedWorkspace } from '../../../../Flow/ConsentContextProvider';
import { OAuthConsentFeatureGate } from '../../../../FeatureGates/FeatureGates';

// Hardcoded — this is intentionally a temporary internal workaround component.
const BYPASS_SITE =
  getEnv() === 'prod' || getEnv() === 'prod-fedm' ? 'hello.atlassian.net' : 'pug.jira-dev.com';

const messages = defineMessages({
  warningTitle: {
    id: 'oauth.siteSelectorBypass.warning.title',
    defaultMessage: 'Internal workaround — not for production use',
    description: 'Title of the warning banner shown to internal users when the site bypass is available',
  },
  warningDescription: {
    id: 'oauth.siteSelectorBypass.warning.description',
    defaultMessage:
      'Your site list could not be loaded, likely due to a timeout. Enter a site below to bypass. This is an internal workaround only.',
    description: 'Body of the bypass warning banner shown to internal users',
  },
  siteInputLabel: {
    id: 'oauth.siteSelectorBypass.input.label',
    defaultMessage: 'Site',
    description: 'Label for the site input field in the bypass banner',
  },
  confirmButton: {
    id: 'oauth.siteSelectorBypass.confirm',
    defaultMessage: 'Use this site',
    description: 'Button label to confirm the bypass site selection',
  },
});

const wrapperStyles = xcss({
  marginBlockEnd: 'space.200',
});

const inputStyles = xcss({
  borderRadius: 'radius.small',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderColor: 'color.border',
  paddingInline: 'space.100',
  paddingBlock: 'space.075',
  font: 'font.body',
  backgroundColor: 'color.background.input',
  color: 'color.text',
  width: '100%',
});

/**
 * Internal-only bypass banner shown when the feature gate is enabled.
 * Allows internal users to manually select a site when the site list fails to load.
 *
 * ⚠️ This is NOT intended for production end-users. Gate access appropriately.
 */
const SiteBypassBanner = (): ReactElement | null => {
  const bypassEnabled = useFeatureGate(OAuthConsentFeatureGate.SITE_SELECTOR_BYPASS);
  const { setSelectedWorkspace } = useSelectedWorkspace();
  const { formatMessage } = useIntl();
  const [inputValue, setInputValue] = useState(BYPASS_SITE);

  if (!bypassEnabled) {
    return null;
  }

  return (
    <Box xcss={wrapperStyles}>
      <SectionMessage
        appearance="warning"
        title={formatMessage(messages.warningTitle)}
      >
        <Stack space="space.150">
          <Text as="p">
            {formatMessage(messages.warningDescription)}
          </Text>
          <Stack space="space.075">
            <label htmlFor="oauth-consent-site-bypass-input">
              <Text size="small" weight="medium">
                {formatMessage(messages.siteInputLabel)}
              </Text>
            </label>
            {/* Box passes unknown props to the underlying DOM element via ...rest */}
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <Box
              as="input"
              id="oauth-consent-site-bypass-input"
              xcss={inputStyles}
              {...({
                value: inputValue,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  setInputValue(e.target.value),
              } as any)}
            />
          </Stack>
          <Inline>
            <Button
              appearance="primary"
              isDisabled={!inputValue.trim()}
              onClick={() => setSelectedWorkspace(inputValue.trim())}
            >
              {formatMessage(messages.confirmButton)}
            </Button>
          </Inline>
        </Stack>
      </SectionMessage>
    </Box>
  );
};

export default SiteBypassBanner;
